import i18n from '@/i18n/config';
import { useAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { useDisclosure } from '@heroui/react';
import { toast } from 'react-toastify';

import { createContactSection, deleteContactSection, updateContactSection } from '@/store';
import type { ContactSection, ContactSectionPayload } from '@/types';

const createEmptyForm = (): ContactSectionPayload => ({
  name: '',
  description: '',
  roles: []
});

export function useContactSectionForm() {
  const [, createSection] = useAtom(createContactSection);
  const [, updateSection] = useAtom(updateContactSection);
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [editingSection, setEditingSection] = useState<ContactSection | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<ContactSectionPayload>(createEmptyForm());

  const openCreate = useCallback(() => {
    setEditingSection(null);
    setFormData(createEmptyForm());
    onOpen();
  }, [onOpen]);

  const openEdit = useCallback(
    (section: ContactSection) => {
      setEditingSection(section);
      setFormData({
        name: section.name,
        description: section.description ?? '',
        roles: section.roles ? [...section.roles] : []
      });
      onOpen();
    },
    [onOpen]
  );

  const saveSection = useCallback(async () => {
    if (!formData.name.trim()) {
      toast.warning('Section name is required');
      return;
    }

    try {
      setIsSubmitting(true);
      if (editingSection) {
        await updateSection({ id: editingSection.id, payload: formData });
        toast.success(i18n.t('copy.section_updated'));
      } else {
        await createSection(formData);
        toast.success(i18n.t('copy.section_created'));
      }
      onOpenChange();
      setEditingSection(null);
      setFormData(createEmptyForm());
    } catch (error) {
      // handled globally
    } finally {
      setIsSubmitting(false);
    }
  }, [createSection, updateSection, editingSection, formData, onOpenChange]);

  return {
    isOpen,
    onOpen,
    onOpenChange,
    openCreate,
    openEdit,
    saveSection,
    isSubmitting,
    isEditMode: Boolean(editingSection),
    formData,
    setFormData
  };
}

export function useContactSectionDelete() {
  const [, deleteSection] = useAtom(deleteContactSection);
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [pendingSection, setPendingSection] = useState<ContactSection | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const openDelete = useCallback(
    (section: ContactSection) => {
      setPendingSection(section);
      onOpen();
    },
    [onOpen]
  );

  const confirmDelete = useCallback(async () => {
    if (!pendingSection) return;
    try {
      setIsDeleting(true);
      await deleteSection(pendingSection.id);
      toast.success(i18n.t('copy.section_deleted'));
      onOpenChange();
      setPendingSection(null);
    } catch (error) {
      // handled globally
    } finally {
      setIsDeleting(false);
    }
  }, [deleteSection, onOpenChange, pendingSection]);

  return {
    isOpen,
    onOpen,
    onOpenChange,
    openDelete,
    confirmDelete,
    isDeleting,
    pendingSection
  };
}

